import { useSelector } from "react-redux";
import { Field } from "redux-form/immutable";

import {
  Box,
  Button,
  Heading,
  Link,
  Stack,
  Text,
  TextButton
} from "@seneca/senekit";

import { makeLinkToHelpSectionArticle } from "common/links";

import { ErrorMessage as ErrorMessageType } from "services/firebase/auth/errorTranslator";

import { isMobile } from "features/common/utils/functions/mobile";
import { FormTextInput } from "features/forms/components/FormTextInput";
import { useIsPlurallApp } from "features/plurall";
import { getHelpLinkRegionCode } from "features/regions/selectors";
import ErrorMessage from "features/sign-up/components/ErrorMessage";

type Props = {
  sendEmailAgain: () => void;
  handleSubmit: () => void;
  resendCodeError?: string;
  resendCodePending?: boolean;
  resendCodeSuccess?: boolean;
  submitting?: boolean;
  error?: ErrorMessageType;
};

const linkToAllowListEmails = makeLinkToHelpSectionArticle("2498958");

function ResetPasswordSignInForm({
  sendEmailAgain,
  handleSubmit,
  resendCodeError,
  resendCodePending,
  resendCodeSuccess,
  submitting,
  error
}: Props) {
  return (
    <Box dataTest="SignUpFormWrapper">
      <Stack align="center" space="xlarge">
        <Stack align="center" space="xlarge">
          <Heading level="3" align="center">
            Login with your new password
          </Heading>
          <Text size="small" weight="bold" align="center">
            Follow the link in the email to reset your password
          </Text>
        </Stack>
        <Box component="form" onSubmit={handleSubmit} width="100%">
          <Stack space="xlarge" align="center">
            <Box width="100%">
              <Field
                name="email"
                component={FormTextInput}
                type="email"
                label="Email"
                autoFocus={!isMobile()}
                autoCapitalize="none"
              />
            </Box>

            <Box width="100%">
              <Field
                name="password"
                component={FormTextInput}
                type="password"
                label="Password"
              />
            </Box>
          </Stack>
          <ErrorMessage>{error?.message}</ErrorMessage>
          <Button loading={submitting} onClick={() => {}} width="100%">
            Login
          </Button>
        </Box>
        <BottomSection
          sendEmailAgain={sendEmailAgain}
          resendCodePending={resendCodePending}
        />
      </Stack>
    </Box>
  );
}

function BottomSection({ sendEmailAgain, resendCodePending }: any) {
  const region = useSelector(getHelpLinkRegionCode);
  const isPlurallApp = useIsPlurallApp();

  return (
    <Text size="small" align="center">
      Didn't get an email? Check your Spam or Junk folder.{" "}
      {resendCodePending ? (
        <strong>Sending again...</strong>
      ) : (
        <TextButton size="small" onClick={sendEmailAgain}>
          Send again
        </TextButton>
      )}{" "}
      {!isPlurallApp && (
        <>
          or share{" "}
          <Link to={linkToAllowListEmails(region)}>this help article</Link> with
          your teacher or IT department.
        </>
      )}
    </Text>
  );
}

export default ResetPasswordSignInForm;
